import React, { useMemo, useState } from 'react';
import { filterFns, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import sortingUtils from '../../../utils/sorting.utils';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import ReagentsModal from '../../Capsules/ReagentsModal';



const CapsuleDetailsTable = ({ configuration, initialConfiguration, showValidationColumn }) => {
    const [sorting, setSorting] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({
        'mixture.category': false,
    });
    const columns = [
        {
            id: 'mixture.name',
            accessorFn: row => row.mixture.name,
            header: () => <span>Nom</span>,
            cell: info => <span className={`${info.row.original.mixture.category === 'Effluences' ? 'text-accent' : ''}`}>{info.getValue()}</span>,
            footer: props => props.column.id,
            filterFn: filterFns.includesString,
            sortingFn: sortingUtils.sortText,
            size: 50
        },
        {
            id: 'sensibility',
            accessorFn: row => {
                if (!initialConfiguration || !initialConfiguration.content) return '-';
                const initialRow = initialConfiguration.content.find(
                    initRow => initRow.mixture.id === row.mixture.id
                );
                return initialRow && initialRow.sensibility !== null ? initialRow.sensibility : '-';
            },
            header: () => <span>Sensibilité</span>,
            cell: info => {
                const value = info.getValue();
                const className = value !== '-' ? 'badge badge-sm badge-primary' : '';
                return <span className={className}>{value}</span>;
            },
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortNumber,
            filterFn: filterFns.inNumberRange,
            enableHiding: true,
            size: 125
        },
        ...(showValidationColumn ? [{
            id: 'validation',
            accessorFn: row => row.sensibility !== null ? row.sensibility : '-',
            header: () => <span>Validation</span>,
            cell: info => {
                const value = info.getValue();
                const className = value !== '-' ? 'badge badge-sm badge-secondary' : '';
                return <span className={className}>{value}</span>;
            },
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortNumber,
            filterFn: filterFns.inNumberRange,
            enableHiding: true,
            size: 125
        }] : []),
        {
            id: 'dilution.label',
            accessorFn: row => row?.dilution?.label || row.mixture?.defaultDilution?.label || '-',
            header: () => <span>Dilution</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'mixture.subCategory',
            accessorFn: row => row.mixture?.subCategory || '-',
            header: () => <span>Catégories</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'mixture.ref',
            accessorFn: row => row.mixture?.ref || '-',
            header: () => <span>Référence</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'mixture.reagents',
            accessorFn: row => row.mixture?.reagents || [],
            header: () => <span>Réactifs</span>,
            cell: (info) => <ReagentsModal mixtureName={info.row.original.mixture?.name} reagents={info.getValue()} description={info.row.original.mixture?.description} />,
            footer: props => props.column.id,
            filterFn: filterFns.arrIncludes,
            enableHiding: true,
        }
    ];

    const tableData = useMemo(() => {
        if (!configuration || !configuration.content) return [];
        let returnedData = [...configuration.content];

        // Tri par défaut par catégorie
        returnedData.sort((a, b) => {
            return sortingUtils.sortCategory(a, b, 'mixture.category', false);
        });

        // Appliquer le tri manuel si défini
        if (sorting.length > 0) {
            const sort = sorting[0];
            const { id, desc } = sort;
            const sortFn = columns.find(column => column.id === id)?.sortingFn;
            if (sortFn && typeof sortFn === 'function') {
                returnedData.sort((a, b) => {
                    return sortFn(a, b, id, desc);
                });
            }
        }

        return returnedData;
    }, [configuration, sorting]);

    const table = useReactTable({
        data: tableData || [],
        columns: columns,
        defaultColumn: {
            size: 75,
            minSize: 20,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        state: {
            sorting,
            columnVisibility,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        manualFiltering: true,
        enableRowSelection: false,
        getRowId: (row, index) => index,
        onColumnVisibilityChange: setColumnVisibility,
    });

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel().rows;

    if (!configuration || !configuration.content || configuration.content.length === 0) {
        return (
            <div className="alert alert-info">
                Aucun contenu disponible pour cette configuration
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="table table-sm w-full">
                <thead>
                    <tr>
                        {headers.map((header) => {
                            const direction = header.column.getIsSorted();
                            const arrow = {
                                asc: <ChevronUpIcon className="w-4 h-4" />,
                                desc: <ChevronDownIcon className="w-4 h-4" />,
                            };
                            const sort_indicator = direction && arrow[direction];

                            return (
                                <th key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }} className="relative">
                                    {header.isPlaceholder ? null : (
                                        <div
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="cursor-pointer flex gap-2 items-center"
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {direction && <span className="text-xs">{sort_indicator}</span>}
                                        </div>
                                    )}
                                    <div
                                        onDoubleClick={() => header.column.resetSize()}
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                        className={`absolute right-0 top-0 h-full w-[5px] bg-black/10 cursor-col-resize select-none touch-none 
                                            ${header.column.getIsResizing() ? 'bg-black/20 opacity-100' : 'hover:bg-black/20'} 
                                            ${table.options.columnResizeDirection}`}
                                    />
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id} className="hover">
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} style={{ width: cell.column.getSize() }}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default CapsuleDetailsTable; 